/* eslint-disable @typescript-eslint/no-explicit-any */
import { Buffer } from 'buffer';

import { FirestoreError } from 'firebase/firestore';
import { BeforeMaskedStateChangeStates } from 'react-input-mask';

import { ConversionEventData, EventData, EventName } from '@types';

import i18n from '../i18n/config';

/**
 * Replaces all occurrences of a search string with a new string in the provided JSON data.
 *
 * @param {string} searchString - The string to search for and replace.
 * @param {string} newString - The string to use as a replacement.
 * @param {unknown} data - The JSON data in which to perform the replacements.
 * @returns {any} The JSON data with the replaced strings.
 */
export const replaceStringInJson = (
  searchString: string,
  newString: string,
  data: unknown
): any => {
  const dataCopy = JSON.parse(JSON.stringify(data));

  const traverse = (obj: any, parentKey?: string): void => {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        const currentKey = parentKey ? `${parentKey}.${key}` : key;

        if (typeof value === 'string') {
          const regex = new RegExp(searchString, 'g');
          obj[key] = value.replace(regex, newString);
        } else if (typeof value === 'object') {
          traverse(value, currentKey);
        }
      }
    }
  };

  traverse(dataCopy);

  return dataCopy;
};

/**
 * Formats a phone number into the format "(XXX) XXX-XXXX".
 *
 * @param {string | undefined} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number or an empty string if phoneNumber is undefined.
 */
export const formatPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) return '';

  const number = phoneNumber.replace(/\D/g, '');

  const areaCode = number.substring(0, 3);
  const firstPart = number.substring(3, 6);
  const secondPart = number.substring(6, 10);

  return `(${areaCode}) ${firstPart}-${secondPart}`;
};

/**
 * Retrieves the TrustedForm URL from the hidden field in the DOM.
 *
 * @returns {string} The TrustedForm URL or an empty string if not found.
 */
export const getTrustedFormUrl = () => {
  const hiddenField = document.getElementsByName(
    'xxTrustedFormCertUrl'
  )[0] as HTMLInputElement;
  const trustedFormUrl = hiddenField?.value;
  return trustedFormUrl ?? '';
};

/**
 * Format's a number into proper currency, e.g.: `1000000` will return: '$1,000,000'
 *
 * @param {number} amount - a dollar amount unformatted
 * @returns {string} returns the dollar amount formatted
 */
export const formatNumberToCurrencyString = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const stripQueryParams = (url: string): string => {
  return url.split('?')[0];
};

/**
 * Extracts the `fbclid` (Facebook click identifier) query param from a URL.
 *
 * @param {string} [url] - The full URL to parse (e.g. the host page URL).
 * @returns {string} The fbclid value, or an empty string if absent or unparseable.
 */
export const getFbclid = (url?: string): string => {
  if (!url) return '';
  try {
    return new URL(url).searchParams.get('fbclid') ?? '';
  } catch {
    return '';
  }
};

/**
 * Escapes text and wraps it in a span LogRocket redacts from session replay, for PII
 * substituted into HTML templates (e.g. the TCPA name). `data-logrocket-hidden` is
 * registered in `privateAttributeBlocklist` (see main.tsx).
 */
export function logRocketHiddenHtml(text: string): string {
  if (!text) return '';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  return `<span data-logrocket-hidden>${escaped}</span>`;
}

export function replacePlaceholders(
  template: string,
  values: Record<string, string>
): string {
  return template.replace(/{{(.*?)}}/g, (_, key) => values[key.trim()] ?? '');
}

/**
 * Currency masking function to format the input according to the currency format.
 *
 * @param {BeforeMaskedStateChangeStates} nextState - The next state of the input.
 * @returns {BeforeMaskedStateChangeResult} The result after applying the currency mask.
 */
export const currencyMask = ({ nextState }: BeforeMaskedStateChangeStates) => {
  const { value } = nextState || {};

  const amountFormatted = value?.replace?.(/^0+/g, '');

  if (amountFormatted?.length === 2) {
    return {
      ...nextState,
      value: amountFormatted,
      selection: {
        start: amountFormatted.length,
        end: amountFormatted.length,
      },
    };
  }

  const amountFormattedWithCommas = amountFormatted?.replace?.(
    /(\d)(?=(\d{3})+(?!\d))/g,
    '$1,'
  );

  if (amountFormattedWithCommas) {
    return {
      ...nextState,
      value: amountFormattedWithCommas,
      selection: {
        start: amountFormattedWithCommas.length,
        end: amountFormattedWithCommas.length,
      },
    };
  }

  return nextState;
};
export const debtCauseOptions = [
  {
    label: 'fields.reducedIncome',
    value: 'REDUCED_INCOME',
  },
  {
    label: 'fields.unemployed',
    value: 'UNEMPLOYED',
  },
  {
    label: 'fields.familyDeath',
    value: 'FAMILY_DEATH',
  },
  {
    label: 'fields.divorceSeparation',
    value: 'DIVORCE_SEPARATION',
  },
  {
    label: 'fields.medical',
    value: 'MEDICAL',
  },
  {
    label: 'fields.moneyManagement',
    value: 'POOR_MONEY_MGMT',
  },
  {
    label: 'fields.other',
    value: 'OTHER',
  },
];
export const creditScoreImportanceOptions = [
  {
    label: 'fields.yes',
    value: 'YES',
  },
  {
    label: 'fields.no',
    value: 'NO',
  },
  {
    label: 'fields.maybe',
    value: 'MAYBE',
  },
];

export const debtGoalOptions = [
  {
    label: 'getOutOfDebtFaster',
    value: 'GET_OUT_DEBT_FASTER',
  },
  {
    label: 'improveCreditScore',
    value: 'IMPROVE_CREDIT_SCORE',
  },
  {
    label: 'reduceMonthlyPayments',
    value: 'REDUCE_MTHLY_PAYMTS',
  },
];

const sanitizeErrorPayload = ({ error }: ConversionEventData) => {
  return {
    error: {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      code: (error as FirestoreError)?.code,
    } as Error,
  };
};

export const sendEventToHost = (
  name: EventName,
  payload?: Omit<EventData, 'name'>
) => {
  const sanitizedPayload =
    name === 'error' && payload
      ? sanitizeErrorPayload(payload.data as ConversionEventData)
      : payload;

  const eventData: EventData = { name, ...sanitizedPayload };
  window.parent.postMessage(eventData, '*');
};

export const translate = (key: string, params?: Record<string, any>) => {
  return i18n.t(key, params);
};

export const shouldShowIntroUI = (
  currentModuleName: string,
  introUI: string | undefined
) => {
  if (currentModuleName === 'MODULE_INTRO') {
    if (introUI == 'none' || introUI == '' || introUI == undefined) {
      return false;
    }
  }
  return true;
};

export const base64Encode = (data: any) =>
  Buffer.from(data, 'utf-8').toString('base64');

export function getCookie(name: string) {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim(); // Remove any leading/trailing whitespace

    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }

  return null;
}

const isPlainObject = (value: unknown): value is Record<string, any> => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

/**
 * Recursively rebuilds a value without any `undefined` entries. Firestore
 * rejects writes containing `undefined`, and partner-supplied passthrough
 * params reach us over postMessage, whose structured clone preserves an
 * explicit `undefined` on a key the host never filled in.
 *
 * Object keys holding `undefined` are dropped outright so consumers fall back
 * to whatever they already have -- the MAPI payload spreads passthrough params
 * over its own base params, so writing an empty value would clobber a real one
 * such as `ckm_subid`. Array entries become `null` instead, because dropping
 * them would shift the index of every entry after the hole. `null`, `''` and
 * `0` are all left alone; only `undefined` is removed.
 *
 * Returns the cleaned value alongside the dot-paths of what was dropped, for
 * logging. Paths only, never values: passthrough params are partner-controlled
 * and may carry PII.
 */
export const stripUndefinedValues = (
  value: any
): { value: any; dropped: string[] } => {
  const dropped: string[] = [];

  const walk = (current: any, path: string): any => {
    if (Array.isArray(current)) {
      return current.map((entry, index) => {
        const entryPath = `${path}[${index}]`;
        if (entry === undefined) {
          dropped.push(entryPath);
          return null;
        }
        return walk(entry, entryPath);
      });
    }

    if (isPlainObject(current)) {
      const cleaned: Record<string, any> = {};
      Object.entries(current).forEach(([key, entry]) => {
        const entryPath = path ? `${path}.${key}` : key;
        if (entry === undefined) {
          dropped.push(entryPath);
          return;
        }
        cleaned[key] = walk(entry, entryPath);
      });
      return cleaned;
    }

    return current;
  };

  return { value: walk(value, ''), dropped };
};
